import React, { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import usePortal from 'react-useportal';
import {
  StyledContainer,
  StyledTaskItem,
  StyledTaskLeft,
  StyledCheckbox,
  StyledTaskType,
  StyledTaskArrow,
  StyledTaskMeta,
  StyledClaimButton,
  StyledEmpty,
  StyledTabs,
  StyledTab,
} from './styled';
import { getTrainingList } from '../../redux/actions/Training';
import { getFriendlyList } from '../../redux/actions/Friendly';
import { claimTasksRewardRequest, getTasksList } from '../../redux/actions/Tasks';
import { getLeaguesList } from '../../redux/actions/League';
import { showPopupWindow } from '../../redux/actions/Popups';
import { getTransferWindow } from '../../redux/selector/Transfers';
import { isLeaguesListLoaded } from '../../redux/selector/League';
import { getPopupByName } from '../../redux/selector/Popups';
import { tasksClaiming } from '../../redux/selector/Tasks';
import { FriendlyPopup, TrainingPopup } from '../Popup';
import { POPUPS } from '../../service/constants';
import { numberWithCommas } from '../../service/utils';
import { ReactComponent as ArrowUpRight } from '../../assets/img/arrow-up-right.svg';

const DAILY_LABELS = {
  0: 'Set your team',
  1: 'Practice',
  2: 'Play friendly match',
};

const SEASON_LABELS = {
  0: 'Friendlies',
  1: 'Practices',
  2: 'Transfers',
};

const progressLabel = (task) => {
  const goal = Number(task.goal);
  const tracker = Number(task.tracker);
  if (Number.isFinite(goal) && goal > 0 && Number.isFinite(tracker)) {
    return `${Math.min(tracker, goal)}/${goal}`;
  }
  if (Number.isFinite(task.remaining) && task.remaining > 0) {
    return `${task.remaining} left`;
  }
  return null;
};

const canClaim = (bucket) =>
  bucket &&
  bucket.total > 0 &&
  bucket.completed >= bucket.total &&
  !bucket.reward_claimed;

const TasksList = ({ tasksPayload }) => {
  const { TRAINING_POPUP, FRIENDLY_POPUP } = POPUPS;
  const leaguesList = useSelector(isLeaguesListLoaded);
  const claiming = useSelector(tasksClaiming);
  const dispatch = useDispatch();
  const history = useHistory();
  const friendlyPopup = useSelector(getPopupByName(FRIENDLY_POPUP));
  const trainingPopup = useSelector(getPopupByName(TRAINING_POPUP));
  const isTransferWindowOpen = useSelector(getTransferWindow);
  const [tab, setTab] = useState('daily');

  const handleDailyClick = (type) => {
    if (type === 0) history.push('/team/strategy');
    if (type === 1) {
      dispatch(showPopupWindow(TRAINING_POPUP, TRAINING_POPUP, true));
      dispatch(getTrainingList());
    }
    if (type === 2) {
      dispatch(showPopupWindow(FRIENDLY_POPUP, FRIENDLY_POPUP, true));
      dispatch(getFriendlyList());
    }
  };

  const handleSeasonClick = (type) => {
    if (type === 0) {
      dispatch(showPopupWindow(FRIENDLY_POPUP, FRIENDLY_POPUP, true));
      dispatch(getFriendlyList());
    }
    if (type === 1) {
      dispatch(showPopupWindow(TRAINING_POPUP, TRAINING_POPUP, true));
      dispatch(getTrainingList());
    }
    if (type === 2) history.push('/transfers/deals');
  };

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });

  useEffect(() => {
    if (isEmpty(leaguesList)) {
      dispatch(getLeaguesList());
    }
  }, [dispatch, leaguesList]);

  const friendlyWasOpen = React.useRef(false);
  const trainingWasOpen = React.useRef(false);
  useEffect(() => {
    const open = !isEmpty(friendlyPopup);
    if (friendlyWasOpen.current && !open) {
      dispatch(getTasksList());
    }
    friendlyWasOpen.current = open;
  }, [dispatch, friendlyPopup]);
  useEffect(() => {
    const open = !isEmpty(trainingPopup);
    if (trainingWasOpen.current && !open) {
      dispatch(getTasksList());
    }
    trainingWasOpen.current = open;
  }, [dispatch, trainingPopup]);

  if (!tasksPayload) {
    return (
      <StyledContainer>
        <StyledEmpty>No tasks yet</StyledEmpty>
      </StyledContainer>
    );
  }

  const { daily, season } = tasksPayload;
  const dailyTasks = Array.isArray(daily?.tasks) ? daily.tasks : [];
  const seasonTasks = Array.isArray(season?.tasks) ? season.tasks : [];
  const showDailyTab = dailyTasks.length > 0;
  const showSeasonTab = seasonTasks.length > 0;
  const activeTab =
    tab === 'season' && showSeasonTab
      ? 'season'
      : showDailyTab
        ? 'daily'
        : showSeasonTab
          ? 'season'
          : null;

  const renderRows = (tasks, labels, onClick, categoryKey) =>
    tasks.map((task, index) => {
      const done = task.status === 1;
      const locked = categoryKey === 'daily' && task.type === 3 && !isTransferWindowOpen;
      const canOpen = !done && !locked;
      const progress = progressLabel(task);
      return (
        <StyledTaskItem
          key={`${categoryKey}-${task.type}-${index}`}
          type="button"
          $done={done}
          $locked={locked}
          disabled={!canOpen}
          onClick={() => {
            if (canOpen) onClick(task.type);
          }}
        >
          <StyledTaskLeft>
            <StyledCheckbox $done={done} aria-hidden="true" />
            <StyledTaskType $muted={done || locked}>
              {labels[task.type] || `Task ${task.type}`}
            </StyledTaskType>
          </StyledTaskLeft>
          {progress ? <StyledTaskMeta $muted={done}>{progress}</StyledTaskMeta> : null}
          {!done && !locked ? (
            <StyledTaskArrow aria-hidden="true">
              <ArrowUpRight />
            </StyledTaskArrow>
          ) : null}
        </StyledTaskItem>
      );
    });

  const renderClaim = (bucket, category) => {
    if (!bucket || !(bucket.total > 0 || bucket.reward != null)) return null;
    const ready = canClaim(bucket);
    const claimed = Boolean(bucket.reward_claimed);
    return (
      <StyledClaimButton
        type="button"
        disabled={!ready || claiming}
        onClick={() => {
          if (ready) dispatch(claimTasksRewardRequest(category));
        }}
      >
        {claimed
          ? 'Claimed'
          : `Claim $${numberWithCommas(bucket.reward)}`}
      </StyledClaimButton>
    );
  };

  return (
    <StyledContainer>
      {!isEmpty(trainingPopup) && (
        <Portal>
          <TrainingPopup popupName={TRAINING_POPUP} leaguesList={leaguesList} />
        </Portal>
      )}
      {!isEmpty(friendlyPopup) && (
        <Portal>
          <FriendlyPopup popupName={FRIENDLY_POPUP} leaguesList={leaguesList} />
        </Portal>
      )}

      {showDailyTab || showSeasonTab ? (
        <StyledTabs role="tablist" aria-label="Task period">
          {showDailyTab ? (
            <StyledTab
              type="button"
              role="tab"
              aria-selected={activeTab === 'daily'}
              $active={activeTab === 'daily'}
              onClick={() => setTab('daily')}
            >
              Daily
              {daily?.total > 0 ? ` · ${daily.completed}/${daily.total}` : ''}
            </StyledTab>
          ) : null}
          {showSeasonTab ? (
            <StyledTab
              type="button"
              role="tab"
              aria-selected={activeTab === 'season'}
              $active={activeTab === 'season'}
              onClick={() => setTab('season')}
            >
              Season
              {season?.total > 0 ? ` · ${season.completed}/${season.total}` : ''}
            </StyledTab>
          ) : null}
        </StyledTabs>
      ) : null}

      {activeTab === 'daily' ? (
        <>
          {renderRows(dailyTasks, DAILY_LABELS, handleDailyClick, 'daily')}
          {renderClaim(daily, 0)}
        </>
      ) : null}

      {activeTab === 'season' ? (
        <>
          {renderRows(seasonTasks, SEASON_LABELS, handleSeasonClick, 'season')}
          {renderClaim(season, 1)}
        </>
      ) : null}

      {!activeTab ? <StyledEmpty>No tasks yet</StyledEmpty> : null}
    </StyledContainer>
  );
};

export default TasksList;
